import { Router } from '@reach/router';
import React, { useContext } from 'react';
import { useApiRequest } from '../../../api/useApiRequest';
import { userHasPermission } from '../../../authorisation/permission';
import { RouteComponentProps } from '../../../helpers/RouteComponentProps';
import { Alert } from '../../../shared/Alert';
import { GetRequest } from '../../../shared/GetRequest';
import { UserContext } from '../../../users/UserContext';
import { DocumentList } from '../DocumentList';
import { GetHandoverPackSectionsResponse } from '../documentResponse';
import { CreateDocument } from '../documents-create/CreateDocument';
import { CreateDocumentCopy } from '../documents-create/CreateDocumentCopy';
import { UpdateDocument } from '../documents-update/UpdateDocument';
import { CreateHandoverPackSection } from '../handover-pack-section/CreateHandoverPackSection';
import { HandoverPackSectionList } from '../handover-pack-section/HandoverPackSectionList';
import { UpdateHandoverPackSection } from '../handover-pack-section/UpdateHandoverPackSection';
import { documentRoutes } from './documentRoutes';
import { HandoverPackSectionProvider } from './HandoverPackSectionProvider';

type DocumentSectionComponents = {
  (props: RouteComponentProps): JSX.Element | null;
};

export const DocumentSection: DocumentSectionComponents = () => {
  const getHandoverPackSectionsRequest = useApiRequest<GetHandoverPackSectionsResponse>({
    httpMethod: 'get',
    endpointUrl: `handover-pack/sections`,
  });

  const userContext = useContext(UserContext);
  const canAccessDocumentManagement = userHasPermission({
    user: userContext.getUser(),
    permission: 'Manually Trigger Parts Update',
  });

  if (!canAccessDocumentManagement) {
    return (
      <Alert
        type="error"
        title="Access Denied"
        body="You do not have sufficient permissions to view this page, please contact administorator or support if you should have permissions"
      />
    );
  }

  return (
    <GetRequest getDataRequest={getHandoverPackSectionsRequest}>
      {({ response, sendRequest }) => (
        <HandoverPackSectionProvider
          handoverPackSections={response.handoverPackSections}
          parts={response.parts}
          refreshContext={sendRequest}
        >
          <Router primary={false}>
            <DocumentList path={documentRoutes.documentList} />
            <CreateDocument path={documentRoutes.createDocument} />
            <UpdateDocument path={documentRoutes.updateDocument} />
            <CreateDocumentCopy path={documentRoutes.createDocumentCopy} />
            <HandoverPackSectionList path={documentRoutes.handoverPackSectionList} />
            <CreateHandoverPackSection path={documentRoutes.createHandoverPackSection} />
            <UpdateHandoverPackSection path={documentRoutes.updateHandoverPackSection} />
          </Router>
        </HandoverPackSectionProvider>
      )}
    </GetRequest>
  );
};
